import {
  BulkStarDocument,
  type BulkStarMutation,
  type BulkStarMutationVariables,
  BulkUnstarDocument,
  type BulkUnstarMutation,
  type BulkUnstarMutationVariables,
  StarDocument,
  type StarMutation,
  type StarMutationVariables,
  type StarsStarableTypeEnum,
  UnstarDocument,
  type UnstarMutation,
  type UnstarMutationVariables,
} from "@src/__generated__/graphql";
import { MutationHelper } from "@src/helpers/apollo/mutation";
import type { AppStore } from "@src/stores/AppStore";
import type { BaseStore } from "@src/stores/BaseStore";
import { QueueState } from "@src/utils/mobx/states/queue-state";
import type { GraphQLFormattedError } from "graphql";
import { action, computed, makeObservable, observable } from "mobx";

export class StarStore implements BaseStore {
  appStore: AppStore;

  private starMutator = new MutationHelper<StarMutation, StarMutationVariables>(
    StarDocument,
  );

  private unstarMutator = new MutationHelper<
    UnstarMutation,
    UnstarMutationVariables
  >(UnstarDocument);

  private bulkStarMutator = new MutationHelper<
    BulkStarMutation,
    BulkStarMutationVariables
  >(BulkStarDocument);

  private bulkUnstarMutator = new MutationHelper<
    BulkUnstarMutation,
    BulkUnstarMutationVariables
  >(BulkUnstarDocument);

  @observable
  private queue: QueueState<{
    starable_id: string;
    starable_type: StarsStarableTypeEnum;
  }>;

  constructor(appStore: AppStore) {
    this.appStore = appStore;
    this.queue = new QueueState(
      (a, b) =>
        a.starable_id === b.starable_id && a.starable_type === b.starable_type,
    );
    makeObservable(this);
  }

  @computed get isBulkStarring() {
    return this.bulkStarMutator.isLoading.value;
  }

  @computed get isBulkUnstarring() {
    return this.bulkUnstarMutator.isLoading.value;
  }

  @computed get isBulkLoading() {
    return this.isBulkStarring || this.isBulkUnstarring;
  }

  @computed
  get isStarring() {
    return (
      starable_id: string,
      starable_type: StarsStarableTypeEnum,
    ): boolean =>
      this.queue.isQueued({ starable_id, starable_type }) &&
      this.starMutator.isLoading.value;
  }

  @computed
  get isUnstarring() {
    return (
      starable_id: string,
      starable_type: StarsStarableTypeEnum,
    ): boolean =>
      this.queue.isQueued({ starable_id, starable_type }) &&
      this.unstarMutator.isLoading.value;
  }

  @computed
  get isLoading() {
    return (
      starable_id: string,
      starable_type: StarsStarableTypeEnum,
    ): boolean =>
      this.isStarring(starable_id, starable_type) ||
      this.isUnstarring(starable_id, starable_type);
  }

  @action.bound
  async star(
    starable_id: string,
    starable_type: StarsStarableTypeEnum,
  ): Promise<
    [StarMutation | null, readonly GraphQLFormattedError[] | Error | null]
  > {
    return this.queue.usingAsync({ starable_id, starable_type }, () =>
      this.starMutator.mutate({
        starable_id,
        starable_type,
      }),
    );
  }

  @action.bound async unstar(
    starable_id: string,
    starable_type: StarsStarableTypeEnum,
  ): Promise<
    [UnstarMutation | null, readonly GraphQLFormattedError[] | Error | null]
  > {
    return this.queue.usingAsync({ starable_id, starable_type }, () =>
      this.unstarMutator.mutate({
        starable_id,
        starable_type,
      }),
    );
  }

  @action.bound async bulkStar(
    starable_ids: string[],
    starable_type: StarsStarableTypeEnum,
  ) {
    return this.queue.usingAsync(
      starable_ids.map((starable_id) => ({ starable_id, starable_type })),
      () =>
        this.bulkStarMutator.mutate({
          starable_ids,
          starable_type,
        }),
    );
  }

  @action.bound async bulkUnstar(
    starable_ids: string[],
    starable_type: StarsStarableTypeEnum,
  ) {
    return this.queue.usingAsync(
      starable_ids.map((starable_id) => ({ starable_id, starable_type })),
      () =>
        this.bulkUnstarMutator.mutate({
          starable_ids,
          starable_type,
        }),
    );
  }
}
